/**
 * src/features/referral/ui/StatusList.tsx
 *
 * Count-based summary of referral status — intentionally NOT a per-invitation row list.
 *
 * Design rationale: GET /api/referral/me returns only aggregate counts (total, pending,
 * signed_up, converted, rewards_granted). The `pending` field is hardcoded to 0 in the
 * API (Phase 24 limitation). Displaying a "0 awaiting" count would mislead users —
 * see RESEARCH Pitfall 5 and REFUI-02 constraint. This component shows only the two
 * meaningful tracked buckets (signed_up + converted) and free months earned.
 *
 * T-27-05: No referee email/identity is rendered — counts only.
 */

import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import type { ReferralStats } from '../hooks/useReferralData';

interface StatusListProps {
  stats: ReferralStats;
}

export const StatusList: FC<StatusListProps> = ({ stats }) => {
  const { t } = useTranslation();

  if (stats.total === 0) {
    return (
      <div className="py-4 text-center space-y-1">
        <p className="text-body font-bold text-slate-700 dark:text-slate-300">
          {t('referral.empty_heading', 'No invitations yet')}
        </p>
        <p className="text-body text-slate-500 dark:text-slate-400">
          {t(
            'referral.empty_body',
            'Share your link — earn a free month for each colleague who subscribes.',
          )}
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {/* Months earned summary */}
      <p className="text-caption font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wide">
        {t('referral.months_earned', '{{n}} free months earned', {
          n: stats.rewards_granted,
        })}
      </p>

      {/* Count buckets: signed up + converted */}
      <div className="flex flex-wrap gap-2">
        <div className="flex items-center gap-2 px-3 py-2 bg-slate-50 dark:bg-slate-800 rounded-xl border border-slate-100 dark:border-slate-700">
          <span
            className="text-micro font-bold px-2 py-0.5 rounded-full bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400"
            role="status"
          >
            {stats.signed_up}
          </span>
          <span className="text-body text-slate-600 dark:text-slate-400">
            {t('referral.status_signed_up', 'Signed up')}
          </span>
        </div>

        <div className="flex items-center gap-2 px-3 py-2 bg-slate-50 dark:bg-slate-800 rounded-xl border border-slate-100 dark:border-slate-700">
          <span
            className="text-micro font-bold px-2 py-0.5 rounded-full bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400"
            role="status"
          >
            {stats.converted}
          </span>
          <span className="text-body text-slate-600 dark:text-slate-400">
            {t('referral.status_converted', 'Converted to paid')}
          </span>
        </div>
      </div>
    </div>
  );
};
